import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useApp } from '@/context/AppContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { money, fmtDate, fmtDateShort } from '@/lib/helpers';
import { Search, ChevronRight, Receipt, IndianRupee, Ban, Plus, Trash2, X, CalendarDays, Store } from 'lucide-react';

const PERIODS = {
  today: 'Today',
  yesterday: 'Yesterday',
  day_before: 'Day Before Yesterday',
  week: 'This Week',
  prev_week: 'Last Week',
  month: 'This Month',
  prev_month: 'Previous Month',
  quarter: 'This Quarter',
  prev_quarter: 'Previous Quarter',
  fy: 'This Fiscal Year',
  prev_fy: 'Previous Fiscal Year',
  all: 'Last 365 Days',
  custom: 'Custom Range',
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

// Returns [start, end] Date bounds (nulls = unbounded). Fiscal year = Apr–Mar. Week = Mon–Sun.
function rangeFor(period, now, customFrom, customTo) {
  const y = now.getFullYear();
  const m = now.getMonth();
  if (period === 'today') return [startOfDay(now), endOfDay(now)];
  if (period === 'yesterday') { const d = addDays(now, -1); return [startOfDay(d), endOfDay(d)]; }
  if (period === 'day_before') { const d = addDays(now, -2); return [startOfDay(d), endOfDay(d)]; }
  if (period === 'week' || period === 'prev_week') {
    const dow = (now.getDay() + 6) % 7; // 0=Mon .. 6=Sun
    const monThis = addDays(now, -dow);
    const mon = period === 'week' ? monThis : addDays(monThis, -7);
    return [startOfDay(mon), endOfDay(addDays(mon, 6))];
  }
  if (period === 'month') return [new Date(y, m, 1), endOfDay(new Date(y, m + 1, 0))];
  if (period === 'prev_month') return [new Date(y, m - 1, 1), endOfDay(new Date(y, m, 0))];
  if (period === 'quarter') { const qs = Math.floor(m / 3) * 3; return [new Date(y, qs, 1), endOfDay(new Date(y, qs + 3, 0))]; }
  if (period === 'prev_quarter') { const qs = Math.floor(m / 3) * 3 - 3; return [new Date(y, qs, 1), endOfDay(new Date(y, qs + 3, 0))]; }
  if (period === 'fy' || period === 'prev_fy') {
    const fyStart = m >= 3 ? y : y - 1;
    const base = period === 'fy' ? fyStart : fyStart - 1;
    return [new Date(base, 3, 1), endOfDay(new Date(base + 1, 2, 31))];
  }
  if (period === 'custom') {
    return [customFrom ? startOfDay(new Date(customFrom)) : null, customTo ? endOfDay(new Date(customTo)) : null];
  }
  return [null, null]; // 'all' / Last 365 Days (handled by fetch window)
}


const saleStatus = (s) => {
  const out = Number(s.outstanding || 0);
  const net = Number(s.net || 0);
  if (out <= 0) return 'PAID';
  if (out >= net) return 'UNPAID';
  return 'PARTIAL';
};
const STATUS_PILL = {
  PAID: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  UNPAID: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200',
  PARTIAL: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
};

export default function SalesHistory() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, stores, currentStore } = useApp();
  const canDelete = user?.role === 'OWNER';
  // Owners and Managers can select invoices and bulk-change their dates; Delete stays Owner-only.
  const canBulk = canDelete || user?.role === 'MANAGER';
  const [q, setQ] = useState('');
  const initialPeriod = (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('period')) || '';
  const [period, setPeriod] = useState(PERIODS[initialPeriod] ? initialPeriod : 'all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [storeScope, setStoreScope] = useState(currentStore || 'ALL');
  // Follow the store picked in the top bar so Sales shows only that store.
  React.useEffect(() => { if (currentStore) setStoreScope(currentStore); }, [currentStore]);
  const [filter, setFilter] = useState('ALL'); // ALL | PAID | UNPAID
  const [selected, setSelected] = useState(() => new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [dating, setDating] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [storeOpen, setStoreOpen] = useState(false);
  const [storing, setStoring] = useState(false);
  const [newStore, setNewStore] = useState('');
  const storeName = (id) => stores.find((s) => s.id === id)?.name || '';

  const { data: sales = [] } = useQuery({
    queryKey: ['sales', q],
    queryFn: async () => (await api.get('/sales', { params: { q, limit: 500 } })).data.data,
  });

  const now = new Date();
  const [rStart, rEnd] = rangeFor(period, now, customFrom, customTo);
  const inPeriod = (iso) => {
    if (period === 'all') return true;
    if (!iso) return false;
    const d = new Date(iso);
    if (rStart && d < rStart) return false;
    if (rEnd && d > rEnd) return false;
    return true;
  };

  const periodSales = useMemo(() => sales.filter((s) => inPeriod(s.created_at) && (storeScope === 'ALL' || s.store_id === storeScope)), [sales, period, storeScope, customFrom, customTo]);

  const totals = useMemo(() => {
    let total = 0, paid = 0, unpaid = 0;
    for (const s of periodSales) {
      const net = Number(s.net || 0);
      const out = Number(s.outstanding || 0);
      total += net; paid += net - out; unpaid += out;
    }
    return { total, paid, unpaid };
  }, [periodSales]);

  const rows = useMemo(() => periodSales.filter((s) => {
    if (filter === 'ALL') return true;
    const st = saleStatus(s);
    if (filter === 'PAID') return st === 'PAID';
    if (filter === 'UNPAID') return st !== 'PAID';
    return true;
  }), [periodSales, filter]);

  const party = (s) => s.customer?.name || (s.customer?.phone ? s.customer.phone : 'Cash Sale');

  // ---- selection + bulk delete ----
  const selectedArr = useMemo(() => [...selected], [selected]);
  const allSelected = rows.length > 0 && rows.every((s) => selected.has(s.invoice_no));
  const toggle = (inv) => setSelected((s) => { const n = new Set(s); n.has(inv) ? n.delete(inv) : n.add(inv); return n; });
  const toggleAll = () => setSelected((s) => (rows.every((r) => s.has(r.invoice_no)) ? new Set() : new Set(rows.map((r) => r.invoice_no))));
  const clearSel = () => setSelected(new Set());
  const doDelete = async () => {
    setDeleting(true);
    try {
      const r = await api.post('/sales/bulk-delete', { invoices: selectedArr });
      const { deleted, restored } = r.data.data;
      toast.success(`${deleted} invoice(s) deleted · ${restored} item(s) returned to stock`);
      qc.invalidateQueries({ queryKey: ['sales'] });
      qc.invalidateQueries({ queryKey: ['stock-summary'] });
      qc.invalidateQueries({ queryKey: ['garments'] });
      clearSel(); setDeleteOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDeleting(false); }
  };

  // ---- bulk change invoice date ----
  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(r.invoice_no)),
    [rows, selected]);
  const openDateDialog = () => {
    // Pre-fill with today's date for convenience.
    setNewDate(new Date().toISOString().slice(0, 10));
    setDateOpen(true);
  };
  const doDateChange = async () => {
    if (!newDate) { toast.error('Please choose a new date.'); return; }
    setDating(true);
    try {
      const r = await api.post('/sales/bulk-update-date', { invoices: selectedArr, new_date: newDate });
      const { updated, skipped } = r.data.data;
      toast.success(`Date updated for ${updated} invoice${updated === 1 ? '' : 's'}${skipped ? ` · ${skipped} skipped` : ''}`);
      qc.invalidateQueries({ queryKey: ['sales'] });
      clearSel(); setDateOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDating(false); }
  };

  // ---- bulk change selling store (Owner only) ----
  const selectedStoreIds = useMemo(
    () => [...new Set(selectedRows.map((r) => r.store_id).filter(Boolean))],
    [selectedRows]);
  const openStoreDialog = () => {
    // Default: if all selected invoices share one store, pre-pick a different store.
    let def = '';
    if (selectedStoreIds.length === 1) {
      const other = stores.find((s) => s.id !== selectedStoreIds[0]);
      def = other ? other.id : '';
    }
    setNewStore(def);
    setStoreOpen(true);
  };
  const doStoreChange = async () => {
    if (!newStore) { toast.error('Please choose a destination store.'); return; }
    if (storing) return; // guard against double-submit
    setStoring(true);
    try {
      const r = await api.post('/sales/bulk-update-store', { invoices: selectedArr, store_id: newStore });
      const { updated, skipped } = r.data.data;
      toast.success(`Store updated for ${updated} invoice${updated === 1 ? '' : 's'}${skipped ? ` · ${skipped} skipped` : ''}`);
      qc.invalidateQueries({ queryKey: ['sales'] });
      clearSel(); setStoreOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setStoring(false); }
  };
  return (
    <Page>
      <TopBar title="Sales Invoices" right={
        <Button size="sm" className="h-9 rounded-full" data-testid="sales-create-invoice" onClick={() => navigate('/billing')}>
          <Plus className="h-4 w-4 lg:mr-1.5" /><span className="hidden lg:inline">Create Sales Invoice</span>
        </Button>
      } />

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 px-4 pt-4 lg:grid-cols-4 lg:px-6">
        <SummaryCard testid="sales-total-card" active={filter === 'ALL'} onClick={() => setFilter('ALL')}
          icon={Receipt} label="Total Sales" value={money(totals.total)} tone="primary" />
        <SummaryCard testid="sales-paid-card" active={filter === 'PAID'} onClick={() => setFilter('PAID')}
          icon={IndianRupee} label="Paid" value={money(totals.paid)} tone="emerald" />
        <SummaryCard testid="sales-unpaid-card" active={filter === 'UNPAID'} onClick={() => setFilter('UNPAID')}
          icon={IndianRupee} label="Unpaid" value={money(totals.unpaid)} tone="rose" />
        <SummaryCard testid="sales-cancelled-card" active={false} onClick={() => {}}
          icon={Ban} label="Cancelled" value="–" tone="muted" disabled />
      </div>

      {/* Filters */}
      <div className="mt-4 flex flex-wrap items-center gap-2 px-4 lg:px-6">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Invoice, mobile, barcode" className="h-11 rounded-xl pl-9" data-testid="sales-search" />
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="h-11 w-[170px] rounded-xl" data-testid="sales-period"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(PERIODS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        {period === 'custom' && (
          <div className="flex items-center gap-1.5" data-testid="sales-custom-range">
            <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-11 w-[150px] rounded-xl" data-testid="sales-custom-from" />
            <span className="text-xs text-muted-foreground">to</span>
            <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-11 w-[150px] rounded-xl" data-testid="sales-custom-to" />
          </div>
        )}
        {stores.length > 1 && (
          <Select value={storeScope} onValueChange={setStoreScope}>
            <SelectTrigger className="h-11 w-[150px] rounded-xl" data-testid="sales-store-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Stores</SelectItem>
              {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Selection bar */}
      {canBulk && selected.size > 0 && (
        <div className="mx-4 mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary/40 bg-primary/5 px-3 py-2 lg:mx-6" data-testid="sales-selection-bar">
          <span className="text-sm font-medium" data-testid="sales-selection-count">{selected.size} selected</span>
          {canDelete && <span className="text-xs text-muted-foreground">Deleting an invoice returns its items to stock.</span>}
          <div className="ml-auto flex flex-wrap justify-end gap-2">
            <Button size="sm" variant="secondary" className="h-8 rounded-lg" data-testid="sales-selection-change-date" onClick={openDateDialog}><CalendarDays className="mr-1 h-3.5 w-3.5" /> Change Date</Button>
            {canDelete && <Button size="sm" variant="secondary" className="h-8 rounded-lg" data-testid="sales-selection-change-store" onClick={openStoreDialog}><Store className="mr-1 h-3.5 w-3.5" /> Change Store</Button>}
            {canDelete && <Button size="sm" variant="secondary" className="h-8 rounded-lg text-destructive" data-testid="sales-selection-delete" onClick={() => setDeleteOpen(true)}><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete</Button>}
            <Button size="sm" variant="ghost" className="h-8 rounded-lg" data-testid="sales-selection-clear" onClick={clearSel}><X className="mr-1 h-3.5 w-3.5" /> Clear</Button>
          </div>
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden px-6 py-4 lg:block">
        {rows.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">No sales found.</p> :
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <Table data-testid="sales-table">
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  {canBulk && <TableHead className="w-10"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="sales-select-all" aria-label="Select all" /></TableHead>}
                  <TableHead>Date</TableHead>
                  <TableHead>Invoice Number</TableHead>
                  <TableHead>Store</TableHead>
                  <TableHead>Party Name</TableHead>
                  <TableHead>Due In</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((s) => {
                  const st = saleStatus(s);
                  return (
                    <TableRow key={s.id} data-testid={`sale-row-${s.invoice_no}`} data-state={selected.has(s.invoice_no) ? 'selected' : undefined} className="cursor-pointer"
                      onClick={() => navigate(`/invoice/${s.invoice_no}`)}>
                      {canBulk && <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(s.invoice_no)} onCheckedChange={() => toggle(s.invoice_no)} data-testid={`sale-select-${s.invoice_no}`} /></TableCell>}
                      <TableCell className="whitespace-nowrap text-sm">{fmtDateShort(s.created_at)} <span className="text-muted-foreground">{new Date(s.created_at).getFullYear()}</span></TableCell>
                      <TableCell className="font-mono text-sm font-medium">{s.invoice_no}</TableCell>
                      <TableCell><span className="inline-block rounded-md bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">{storeName(s.store_id)}</span></TableCell>
                      <TableCell>{party(s)}</TableCell>
                      <TableCell className="text-muted-foreground">-</TableCell>
                      <TableCell className="text-right font-mono font-semibold tabular-nums">{money(s.net)}</TableCell>
                      <TableCell><span className={`inline-block rounded-md px-2 py-0.5 text-xs font-medium ${STATUS_PILL[st]}`}>{st === 'PAID' ? 'Paid' : st === 'PARTIAL' ? 'Partial' : 'Unpaid'}</span></TableCell>
                      <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>}
      </div>

      {/* Mobile card list */}
      <div className="px-4 py-3 lg:hidden">
        <div className="space-y-2" data-testid="sales-list">
          {rows.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">No sales found.</p> :
            rows.map((s) => {
              const st = saleStatus(s);
              return (
                <div key={s.id} className="flex items-center gap-2 rounded-xl border border-border bg-card p-3">
                  {canBulk && <Checkbox checked={selected.has(s.invoice_no)} onCheckedChange={() => toggle(s.invoice_no)} data-testid={`sale-mselect-${s.invoice_no}`} />}
                  <button onClick={() => navigate(`/invoice/${s.invoice_no}`)} data-testid={`sale-${s.invoice_no}`} className="flex flex-1 items-center justify-between text-left">
                    <div><p className="font-mono text-sm font-semibold">{s.invoice_no}</p><p className="text-xs text-muted-foreground">{s.items.length} items · {party(s)}</p><p className="text-xs text-muted-foreground">{fmtDate(s.created_at)} · {storeName(s.store_id)}</p></div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <p className="font-mono text-sm font-semibold">{money(s.net)}</p>
                        <span className={`inline-block rounded-md px-1.5 py-0.5 text-[10px] font-medium ${STATUS_PILL[st]}`}>{st === 'PAID' ? 'Paid' : st === 'PARTIAL' ? 'Partial' : 'Unpaid'}</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </button>
                </div>
              );
            })}
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="sales-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} invoice{selected.size === 1 ? '' : 's'}?</AlertDialogTitle>
            <AlertDialogDescription>The sold items on these invoices will be returned to AVAILABLE stock. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doDelete(); }} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="sales-delete-confirm">{deleting ? 'Deleting…' : 'Delete'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change invoice date dialog */}
      <Dialog open={dateOpen} onOpenChange={(o) => { if (!dating) setDateOpen(o); }}>
        <DialogContent data-testid="sales-change-date-dialog">
          <DialogHeader>
            <DialogTitle>Change invoice date</DialogTitle>
            <DialogDescription>
              Set a new date for <b data-testid="change-date-count">{selected.size}</b> selected invoice{selected.size === 1 ? '' : 's'}. Invoice numbers, amounts, items, payments and stock stay unchanged.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Current invoice dates</p>
              <div className="max-h-40 overflow-y-auto rounded-lg border border-border bg-muted/30 p-2" data-testid="change-date-current-list">
                {selectedRows.length === 0
                  ? <p className="px-1 py-1.5 text-xs text-muted-foreground">No invoices selected.</p>
                  : selectedRows.map((s) => (
                    <div key={s.invoice_no} className="flex items-center justify-between px-1 py-1 text-sm">
                      <span className="font-mono text-xs font-medium">{s.invoice_no}</span>
                      <span className="text-xs text-muted-foreground">{fmtDate(s.created_at)}</span>
                    </div>
                  ))}
              </div>
            </div>

            <div>
              <Label htmlFor="sales-new-date" className="text-sm font-medium">New date</Label>
              <Input id="sales-new-date" type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
                className="mt-1 h-11 rounded-xl" data-testid="sales-new-date-input" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" className="rounded-xl" onClick={() => setDateOpen(false)} disabled={dating} data-testid="change-date-cancel">Cancel</Button>
            <Button className="rounded-xl" onClick={doDateChange} disabled={dating || !newDate || selected.size === 0} data-testid="change-date-apply">
              {dating ? 'Applying…' : 'Apply to All Selected Invoices'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change selling store dialog (Owner only) */}
      <Dialog open={storeOpen} onOpenChange={(o) => { if (!storing) setStoreOpen(o); }}>
        <DialogContent data-testid="sales-change-store-dialog">
          <DialogHeader>
            <DialogTitle>Change selling store</DialogTitle>
            <DialogDescription>
              Re-file <b data-testid="change-store-count">{selected.size}</b> selected invoice{selected.size === 1 ? '' : 's'} under a different store. This corrects where the sale was recorded — the invoice number, items, prices, payments and dues are preserved and nothing is duplicated.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">Current store{selectedStoreIds.length === 1 ? '' : 's'}</p>
              <p className="text-sm font-medium" data-testid="change-store-current">
                {selectedStoreIds.length === 0 ? '—' : selectedStoreIds.map(storeName).filter(Boolean).join(', ')}
              </p>
            </div>

            <div>
              <Label className="text-sm font-medium">Transfer to</Label>
              <Select value={newStore} onValueChange={setNewStore}>
                <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="change-store-select"><SelectValue placeholder="Select a store" /></SelectTrigger>
                <SelectContent>
                  {stores.map((s) => (
                    <SelectItem key={s.id} value={s.id} disabled={selectedStoreIds.length === 1 && selectedStoreIds[0] === s.id} data-testid={`change-store-opt-${s.id}`}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-start gap-2 rounded-xl bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300" data-testid="change-store-warning">
              <Ban className="mt-0.5 h-4 w-4 shrink-0" />
              <span>This moves the sale, profit, payments, returns and the invoice's stock to the selected store. Please confirm the store is correct — this applies to every selected invoice.</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" className="rounded-xl" onClick={() => setStoreOpen(false)} disabled={storing} data-testid="change-store-cancel">Cancel</Button>
            <Button className="rounded-xl" onClick={doStoreChange} disabled={storing || !newStore || selected.size === 0} data-testid="change-store-apply">
              {storing ? 'Applying…' : 'Confirm Store Change'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}

const TONE = {
  primary: 'text-primary',
  emerald: 'text-emerald-600',
  rose: 'text-rose-600',
  muted: 'text-muted-foreground',
};
const SummaryCard = ({ icon: Icon, label, value, tone, active, onClick, testid, disabled }) => (
  <button type="button" data-testid={testid} onClick={onClick} disabled={disabled}
    className={`rounded-2xl border bg-card p-4 text-left transition-colors ${active ? 'border-primary ring-1 ring-primary' : 'border-border'} ${disabled ? 'cursor-default opacity-70' : 'hover:border-primary/60'}`}>
    <div className={`flex items-center gap-2 ${TONE[tone]}`}>
      <Icon className="h-4 w-4" />
      <span className="text-xs font-medium">{label}</span>
    </div>
    <div className="mt-2 font-mono text-xl font-semibold tabular-nums lg:text-2xl">{value}</div>
  </button>
);
